import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useUscgMapContext } from './UscgMapContext';

/**
 * Incident-map overlay pill (thehub HubMapVisibleItemsPill pattern): count of
 * visible layers, search, per-item remove, clear all.
 */
export function MapVisibleLayersPill() {
  const {
    visibleMapLayerItems,
    toggleExternalLayerVisibility,
    toggleInternalLayerVisibility,
  } = useUscgMapContext();

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const visibleItems = useMemo(
    () =>
      visibleMapLayerItems.map((item) => ({
        ...item,
        onRemove: () =>
          item.kind === 'External'
            ? toggleExternalLayerVisibility(item.id)
            : toggleInternalLayerVisibility(item.id),
      })),
    [
      visibleMapLayerItems,
      toggleExternalLayerVisibility,
      toggleInternalLayerVisibility,
    ]
  );

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return visibleItems;
    return visibleItems.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.kind.toLowerCase().includes(q)
    );
  }, [visibleItems, searchQuery]);

  if (visibleItems.length === 0) {
    return null;
  }

  const clearAll = () => {
    for (const item of visibleItems) {
      item.onRemove();
    }
    setOpen(false);
    setSearchQuery('');
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setSearchQuery('');
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          <Button
            type="button"
            size="sm"
            variant="rounded"
            id="map-visible-layers-pill"
            aria-label={`${visibleItems.length} map layer${
              visibleItems.length === 1 ? '' : 's'
            } visible. Open to manage.`}
          />
        }
      >
        <SharedIcon iconName="stacks" size="sm" />
        {visibleItems.length} on map
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="dark tw:w-80 tw:p-0 tw:text-foreground"
      >
        <div className="tw:flex tw:flex-col tw:gap-2 tw:border-b tw:border-border tw:px-3 tw:py-2">
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
            <span className="tw:text-sm tw:font-medium">Map layers</span>
            <Badge variant="secondary">{visibleItems.length} visible</Badge>
          </div>
          <Input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search layers"
            aria-label="Search layers"
          />
        </div>

        <div className="tw:max-h-64 tw:overflow-y-auto tw:px-2 tw:py-2">
          {filteredItems.length === 0 ? (
            <p className="tw:mb-0 tw:py-4 tw:text-center tw:text-sm tw:text-muted-foreground">
              No layers match your search.
            </p>
          ) : (
            <ul className="tw:mb-0 tw:list-none tw:p-0">
              {filteredItems.map((item) => (
                <li
                  key={`${item.kind}-${item.id}`}
                  className="tw:flex tw:items-start tw:gap-2 tw:rounded tw:px-2 tw:py-2"
                >
                  <div className="tw:min-w-0 tw:flex-1">
                    <div className="tw:mb-1 tw:flex tw:flex-wrap tw:items-center tw:gap-1">
                      <Badge variant="secondary">{item.kind}</Badge>
                    </div>
                    <div className="tw:truncate tw:text-sm" title={item.name}>
                      {item.name}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove ${item.name} from map`}
                    onClick={() => item.onRemove()}
                  >
                    <SharedIcon iconName="close" size="sm" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="tw:border-t tw:border-border tw:px-3 tw:py-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="tw:w-full"
            onClick={clearAll}
          >
            Clear all layers
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
